import React, { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Divider,
  Tabs,
  Tab,
  Button
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

const JsonEditor = ({ selectedEndpoint, jsonData, setJsonData, isMobile, jsonFieldRefs, switchTab, activeTab }) => {
  const [tab, setTab] = useState("body");
  const safeFormData = Array.isArray(jsonData) ? jsonData : [];

  const handleTabChange = (_, newVal) => setTab(newVal);

  const scrollToField = (key) => {
    if (switchTab) switchTab("params"); // 🔹 Switch tab
    setTimeout(() => {
      const field = jsonFieldRefs.current[key];
      if (field) {
        field.scrollIntoView({ behavior: "smooth", block: "center" });
        field.focus();
      }
    }, 100);
  };
  

  const body = selectedEndpoint?.request?.body || {};
  const mode = body?.mode || "raw";
  const isFormData = mode !== "raw";
  const isJson = mode === "raw";

  const formDataObject = (() => {
    if (!isFormData) return {};
    const formArray = body[mode] || [];
    return Object.fromEntries(formArray.map((item) => [item.key, item.value || ""]));
  })();

  const handleChange = (keyPath, value) => {
    if (isJson) {
      setJsonData((prev) => {
        const newData = Array.isArray(prev) ? [...prev] : { ...prev };
        const keys = keyPath.split(".");
        let obj = newData;

        keys.slice(0, -1).forEach((k) => {
          if (Array.isArray(obj)) {
            const idx = parseInt(k, 10);
            obj[idx] = Array.isArray(obj[idx]) ? [...obj[idx]] : { ...obj[idx] };
            obj = obj[idx];
          } else {
            obj[k] = Array.isArray(obj[k]) ? [...obj[k]] : { ...obj[k] };
            obj = obj[k];
          }
        });

        const lastKey = keys[keys.length - 1];
        if (Array.isArray(obj)) {
          obj[parseInt(lastKey, 10)] = value;
        } else {
          obj[lastKey] = value;
        }

        return newData;
      });
    } else {
      const updatedArray = (body[mode] || []).map((item) =>
        item.key === keyPath ? { ...item, value } : item
      );
      setJsonData(updatedArray);
    }
  };

  

  const handleCopy = () => {
    if (isJson) {
      navigator.clipboard.writeText(JSON.stringify(jsonData, null, 2));
    } else {
      const queryString = Object.entries(formDataObject)
        .map(([key, val]) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`)
        .join("&");
      navigator.clipboard.writeText(queryString);
    }
  };

  const renderFields = (data, parentKey = "") => {
    return Object.entries(data).map(([key, value]) => {
      const fieldKey = parentKey ? `${parentKey}.${key}` : key;
  
      if (typeof value === "object" && value !== null) {
        // recurse for nested objects
        return (
          <Box key={fieldKey} sx={{ ml: 2 }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>{key}</Typography>
            {renderFields(value, fieldKey)}
          </Box>
        );
      }
  
      if (jsonFieldRefs && isJson) {
        jsonFieldRefs.current[fieldKey] =
          jsonFieldRefs.current[fieldKey] || React.createRef();
      }
  
      return (
        <Box key={fieldKey} sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography sx={{ minWidth: 200,fontWeight: 500 }}>{fieldKey}</Typography>
          <TextField
            inputRef={isJson ? (el) => (jsonFieldRefs.current[fieldKey] = el) : null}
            value={value}
            fullWidth
            onChange={(e) => handleChange(fieldKey, e.target.value)}
            sx={{
              height: 30,
              width: 300,
              "& .MuiInputBase-root": { height: 30 },
            }}
          />
        </Box>
      );
    });
  };


  const renderFormDataFields = (formArray) => {
    return formArray.map((item, index) => (
      <Box key={index} sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography sx={{ minWidth: 200, fontWeight: 500 }}>{item.key}</Typography>
  
        {item.type === "file" ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button variant="outlined" component="label" size="small">
              Upload
              <input
                type="file"
                hidden
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  setJsonData((prev) => {
                    const updated = [...prev];
                    updated[index] = { ...updated[index], value: file };
                    return updated;
                  });
                }}
              />
            </Button>
        
            {/* ✅ Show filename if file is selected */}
            {item.value && item.value.name && (
              <Typography variant="body2" color="text.secondary">
                {item.value.name}
              </Typography>
            )}
          </Box>
        ) : (
          <TextField
            value={item.value || ""}
            onChange={(e) => {
              const { value } = e.target;
              setJsonData((prev) => {
                const updated = [...prev];
                updated[index] = { ...updated[index], value }; // ✅ clone object
                return updated;
              });
            }}
            sx={{
              height: 30,
              width: 300,
              "& .MuiInputBase-root": { height: 30 },
            }}
          />
        )}
      </Box>
    ));
  };
  
  

  return (
    <Grid container spacing={2} position="relative">
      {/* Tab Navigation */}
      <Grid item xs={12}>
        <Tabs value={activeTab} onChange={(_, newVal) => switchTab(newVal)} textColor="primary">
          <Tab label="Body" value="body" />
          <Tab label="Params" value="params" />
        </Tabs>
        
        {!isMobile && (
          <Typography
              sx={{ fontSize: "16px", fontWeight: 400, lineHeight: "20px", color: "#4b5563", mb: 1, textAlign: "right", position: "absolute", right: 0, top : 40 }}>
              {isJson ? "application/json" : "multipart/form-data"}
          </Typography>
        )}
        
        <Box sx={{ position: "absolute", top: 82, right: 6, zIndex: 9, display:"flex", alignItems:"center", gap: 1.5, }}>
          <Tooltip title="Copy">
            <IconButton onClick={handleCopy} sx={{ display: "flex", alignItems: "center", gap: "3px", "&:hover": {background: "none",} }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_108_1337)">
                <path d="M6 10C6 8.11438 6 7.17157 6.58579 6.58579C7.17157 6 8.11438 6 10 6L10.6667 6C12.5523 6 13.4951 6 14.0809 6.58579C14.6667 7.17157 14.6667 8.11438 14.6667 10V10.6667C14.6667 12.5523 14.6667 13.4951 14.0809 14.0809C13.4951 14.6667 12.5523 14.6667 10.6667 14.6667H10C8.11438 14.6667 7.17157 14.6667 6.58579 14.0809C6 13.4951 6 12.5523 6 10.6667L6 10Z" stroke="#686868" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M11.3332 6.00001C11.3316 4.02862 11.3018 3.00748 10.7279 2.3083C10.6171 2.17327 10.4933 2.04946 10.3583 1.93865C9.62074 1.33334 8.52491 1.33334 6.33325 1.33334C4.1416 1.33334 3.04577 1.33334 2.3082 1.93865C2.17318 2.04946 2.04937 2.17327 1.93856 2.30829C1.33325 3.04586 1.33325 4.14169 1.33325 6.33334C1.33325 8.525 1.33325 9.62082 1.93856 10.3584C2.04937 10.4934 2.17318 10.6172 2.3082 10.728C3.00739 11.3018 4.02852 11.3317 5.99992 11.3333" stroke="#686868" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                <clipPath id="clip0_108_1337">
                <rect width="16" height="16" fill="white"/>
                </clipPath>
                </defs>
              </svg>
              <Typography
                  sx={{ fontSize: "12px", fontWeight: 400, lineHeight: "20px", color: "#686868",}}>Copy
              </Typography>
            </IconButton>
          </Tooltip>
        </Box>
        <Divider />
      </Grid>

      {/* Body Tab */}
      {activeTab === "body" && (
        <Grid item xs={12} position="relative" >
            <Box sx={{ color: "#1d1d1d", overflowY: "auto", border: "1px solid #DFDFDF", borderRadius: "10px", }}>
                {isJson ? (
                    <SyntaxHighlighter language="json" style={oneLight} customStyle={{ margin: 0, background: "#ECF2FE", }} codeTagProps={{
                      style: { background: "#ECF2FE" }
                    }}>
                        {JSON.stringify(jsonData, null, 2)}
                    </SyntaxHighlighter>
                ) : (
                    <Box>
                        {Object.entries(formDataObject).map(([key, value]) => (
                            <Typography key={key} sx={{ color: "#1D1D1D", mb: 0.5 }}>
                                <strong>{key}</strong>: {value}
                            </Typography>
                        ))}
                    </Box>
                )}
            </Box>
        </Grid>
      )}

      {/* Params Tab */}
      {activeTab === "params" && (
        <Grid item xs={12}>
          <Box
            sx={{
              padding: "16px",
              background: "#ffffff",
              overflowY: "auto",
              border: "1px solid #DFDFDF",
              borderRadius: "10px",
            }}
          >
            {isJson ? renderFields(jsonData) : renderFormDataFields(safeFormData) }
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default JsonEditor;
